import React from "react";
import { ArrowRight, ArrowUpRight } from "lucide-react";
import { SERVICES, INDUSTRIES } from "../data/content";
import { useConsultation } from "../components/ConsultationContext";
import Reveal from "../components/Reveal";

export default function Services() {
  const consult = useConsultation();
  const [hoveredIndustry, setHoveredIndustry] = React.useState(null);
  return (
    <div data-testid="services-page">
      <section className="bg-[#0A192F] text-white pt-44 pb-40 relative overflow-hidden min-h-[80vh] flex items-end">
        <div className="absolute -top-40 -right-40 h-[40rem] w-[40rem] rounded-full bg-[#C5A059]/8 blur-[140px]" aria-hidden />
        <div className="absolute inset-0 grain pointer-events-none" aria-hidden />
        <div className="container-x relative">
          <p className="eyebrow text-[#C5A059]">Our Professional Services</p>
          <h1 className="editorial-display text-[clamp(2.8rem,9vw,9.5rem)] mt-12 leading-[0.96] max-w-6xl">
            Taxation, audit, compliance <br /><span className="italic text-[#C5A059]">and advisory.</span>
          </h1>
          <p className="text-white/65 mt-16 max-w-2xl text-lg lg:text-xl leading-[1.7]">
            The Firm provides a comprehensive range of professional services.
          </p>
        </div>
      </section>

      <section className="bg-white">
        <div className="container-x py-32 lg:py-44">
          {SERVICES.map((s, idx) => (
            <Reveal key={s.slug} data-testid={`service-row-${s.slug}`}>
              <article className={`py-32 lg:py-44 ${idx !== 0 ? "border-t border-[#0A192F]/10" : ""}`}>
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start">
                  <div className="lg:col-span-3">
                    <p className="font-serif italic text-[#C5A059] text-3xl lg:text-4xl">— {String(idx+1).padStart(2,"0")}</p>
                    <p className="overline text-[#64748B] text-[10px] mt-6">Practice area</p>
                  </div>
                  <div className="lg:col-span-9">
                    <h2 className="editorial-display text-[#0A192F] text-[clamp(2.6rem,7vw,7rem)] leading-[0.96]">
                      {s.title}.
                    </h2>
                    <p className="text-[#475569] mt-12 lg:mt-16 text-lg lg:text-xl leading-[1.85] max-w-[60ch]">
                      {s.summary}
                    </p>

                    <div className="mt-20 lg:mt-24 grid grid-cols-1 md:grid-cols-2 gap-y-8 gap-x-12 max-w-3xl border-t border-[#0A192F]/10 pt-12">
                      {s.points.map((p, i) => (
                        <div key={p} className="flex items-start gap-5">
                          <span className="font-serif italic text-[#C5A059] text-lg flex-shrink-0 mt-0.5">{String(i+1).padStart(2,"0")}</span>
                          <p className="text-[#0A192F]/85 text-[1rem] leading-[1.65]">{p}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </article>
            </Reveal>
          ))}
        </div>
      </section>

      {/* ============= OUR CLIENTELE — moved from Home ============= */}
      <section data-testid="industries-section" className="bg-[#0A192F] text-white relative">
        <div className="absolute inset-0 overflow-hidden pointer-events-none" aria-hidden>
          <div className="absolute -top-40 -right-40 h-[44rem] w-[44rem] rounded-full bg-[#C5A059]/6 blur-[140px]" />
        </div>

        <div className="relative container-x py-44 lg:py-64">
          <Reveal>
            <p className="eyebrow text-[#C5A059]">Our Clientele</p>
            <h2 className="editorial-display text-white text-[clamp(2.6rem,7vw,7rem)] mt-14 leading-[0.96] max-w-[20ch]">
              Tailored solutions for a <span className="italic text-[#C5A059]">diverse clientele.</span>
            </h2>
            <p className="mt-12 text-white/65 text-lg lg:text-xl leading-[1.85] max-w-[60ch]">
              The Firm serves a diverse range of clients across multiple sectors, delivering tailored solutions based on their specific requirements.
            </p>
          </Reveal>

          <div className="mt-28 lg:mt-40 grid grid-cols-1 lg:grid-cols-12 gap-16 items-start">
            <div className="lg:col-span-7">
              <ul className="border-t border-white/12">
                {INDUSTRIES.map((ind, i) => (
                  <Reveal key={ind.slug} delay={i * 70}>
                    <li onMouseEnter={() => setHoveredIndustry(i)} onMouseLeave={() => setHoveredIndustry(null)} data-testid={`industry-tile-${ind.slug}`}>
                      <div className="block py-9 lg:py-12 border-b border-white/12 group">
                        <div className="flex items-baseline justify-between gap-8">
                          <div className="flex items-baseline gap-8 lg:gap-14 flex-1">
                            <span className="font-serif italic text-[#C5A059]/70 text-xl lg:text-2xl flex-shrink-0">— {String(i+1).padStart(2,"0")}</span>
                            <h3 className="font-serif text-white text-[clamp(2rem,4.4vw,4.4rem)] leading-[1.0] tracking-[-0.022em] font-light">
                              {ind.title}
                            </h3>
                          </div>
                        </div>
                      </div>
                    </li>
                  </Reveal>
                ))}
              </ul>
            </div>

            <Reveal className="lg:col-span-5 lg:sticky lg:top-32" delay={200}>
              <div className="aspect-[3/4] relative overflow-hidden">
                {INDUSTRIES.map((ind, i) => (
                  <img
                    key={ind.slug}
                    src={ind.image}
                    alt={ind.title}
                    className={`absolute inset-0 w-full h-full object-cover transition-all duration-[1200ms] ease-[cubic-bezier(0.22,1,0.36,1)] grayscale-[8%] ${(hoveredIndustry === i || (hoveredIndustry === null && i === 0)) ? "opacity-90 scale-100" : "opacity-0 scale-105"}`}
                  />
                ))}
                <div className="absolute inset-0 bg-gradient-to-t from-[#0A192F] via-[#0A192F]/30 to-transparent" />
                <div className="absolute inset-0 flex items-end p-10 lg:p-12">
                  <div>
                    <p className="overline text-[#C5A059] text-[10px]">{INDUSTRIES[hoveredIndustry ?? 0].title}</p>
                    <p className="text-white/85 text-sm mt-4 max-w-md leading-relaxed">{INDUSTRIES[hoveredIndustry ?? 0].description}</p>
                  </div>
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>
    </div>
  );
}
